/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Partners Hooks - React Query for Partners
 * ============================================================================
 *
 * Custom hooks for partner data fetching and mutations using React Query.
 *
 * ============================================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { partnersService } from '@/services/superadmin/partners.service';
import {
  PartnerListItem,
  PartnerDetail,
  Partner,
  PartnerReferral,
  CreatePartnerRequest,
  PartnerFilterParams,
} from '@/types/superadmin.types';
import { ApiError, PaginatedResponse, PaginationParams } from '@/services/api/types';

// Query keys for cache management
export const partnerKeys = {
  all: ['partners'] as const,
  lists: () => [...partnerKeys.all, 'list'] as const,
  list: (params?: PaginationParams & PartnerFilterParams) => [...partnerKeys.lists(), params] as const,
  details: () => [...partnerKeys.all, 'detail'] as const,
  detail: (id: string) => [...partnerKeys.details(), id] as const,
  referrals: (id: string) => [...partnerKeys.detail(id), 'referrals'] as const,
  stats: () => [...partnerKeys.all, 'stats'] as const,
};

/**
 * Hook to fetch paginated partners list
 */
export function usePartners(params?: PaginationParams & PartnerFilterParams) {
  return useQuery<PaginatedResponse<PartnerListItem>, ApiError>({
    queryKey: partnerKeys.list(params),
    queryFn: () => partnersService.getPartners(params),
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to fetch single partner detail
 */
export function usePartner(id: string) {
  return useQuery<PartnerDetail, ApiError>({
    queryKey: partnerKeys.detail(id),
    queryFn: () => partnersService.getPartner(id),
    enabled: !!id,
  });
}

/**
 * Hook to fetch partner's referrals
 */
export function usePartnerReferrals(id: string) {
  return useQuery<PartnerReferral[], ApiError>({
    queryKey: partnerKeys.referrals(id),
    queryFn: () => partnersService.getPartnerReferrals(id),
    enabled: !!id,
  });
}

/**
 * Hook for creating partner
 */
export function useCreatePartner() {
  const queryClient = useQueryClient();

  return useMutation<Partner, ApiError, CreatePartnerRequest>({
    mutationFn: partnersService.createPartner,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: partnerKeys.lists() });
    },
  });
}

/**
 * Hook for updating partner
 */
export function useUpdatePartner() {
  const queryClient = useQueryClient();

  return useMutation<Partner, ApiError, { id: string; data: Partial<CreatePartnerRequest> & { status?: string } }>({
    mutationFn: ({ id, data }) => partnersService.updatePartner(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: partnerKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: partnerKeys.lists() });
    },
  });
}

/**
 * Hook for activating partner
 */
export function useActivatePartner() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: partnersService.activatePartner,
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: partnerKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: partnerKeys.lists() });
      queryClient.invalidateQueries({ queryKey: partnerKeys.stats() });
    },
  });
}

/**
 * Hook for suspending partner
 */
export function useSuspendPartner() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, { id: string; reason?: string }>({
    mutationFn: ({ id, reason }) => partnersService.suspendPartner(id, reason),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: partnerKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: partnerKeys.lists() });
      queryClient.invalidateQueries({ queryKey: partnerKeys.stats() });
    },
  });
}

/**
 * Hook for terminating partner
 */
export function useTerminatePartner() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, { id: string; reason?: string }>({
    mutationFn: ({ id, reason }) => partnersService.terminatePartner(id, reason),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: partnerKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: partnerKeys.lists() });
      queryClient.invalidateQueries({ queryKey: partnerKeys.stats() });
    },
  });
}

/**
 * Hook to fetch partner statistics
 */
export function usePartnerStats() {
  return useQuery<{ activePartners: number; totalPendingBalance: number }, ApiError>({
    queryKey: partnerKeys.stats(),
    queryFn: () => partnersService.getPartnerStats(),
    staleTime: 60 * 1000,
  });
}
